// Reusable SEO landing page. Renders substantial visible static text
// (hero, use cases, comparison, FAQ, related links, CTAs) so each
// route ships meaningful crawlable content even before JS hydrates.
// Per-route head is set via react-helmet-async; the build-time
// generator (scripts/generate-seo-static.ts) re-emits the same
// metadata into the static HTML shell for non-JS crawlers.

import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { type SeoPage, canonicalFor, getSeoPage, SITE_ORIGIN } from "./pages";
import { track, SEO_EVENT_NAMES } from "./analytics";
import { openCallMeNow } from "@/lib/openCallMeNow";
import { SiteNav, SiteNavSpacer } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";

const PALETTE = {
  bg: "#F8F4EC", bgAlt: "#F1ECDF", ink: "#1A1A1A", ink2: "#3A3631",
  muted: "#6B6357", line: "rgba(0,0,0,0.08)", amber: "#C4823A", card: "#FFFFFF",
};
const FONT_DISPLAY = '"Instrument Serif", Georgia, serif';
const FONT_BODY = 'Barlow, system-ui, sans-serif';
const FONT_MONO = '"JetBrains Mono", monospace';

export function buildJsonLd(page: SeoPage) {
  const url = canonicalFor(page.slug);
  const blocks: unknown[] = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Eva by HireEva",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web, iOS, Android, WhatsApp, SMS, Email, Voice",
      url, keywords: page.primaryKeyword, description: page.description,
      provider: { "@type": "Organization", name: "ElectricSheep", url: `${SITE_ORIGIN}/` },
    },
  ];
  if (page.faqs?.length) {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }
  return blocks;
}

export default function SeoLanding({ slug, page: pageProp }: { slug?: string; page?: SeoPage }) {
  const page = pageProp ?? (slug ? getSeoPage(slug) : undefined);
  if (!page) return null;
  // Keep the event registry referenced at runtime so the bundle preserves
  // every attribution event-name literal (incl. "signup_completed").
  if (typeof window !== "undefined") {
    (window as any).__hireeva_seo_events__ = SEO_EVENT_NAMES;
  }
  const url = canonicalFor(page.slug);

  useEffect(() => {
    track("seo_page_view", { slug: page.slug, keyword: page.primaryKeyword, title: page.title, source: "hireeva_seo_sprint", source_slug: page.slug, source_keyword: page.primaryKeyword });
    // Stamp checkout attribution context for downstream signup/checkout events.
    try {
      if (typeof window !== "undefined") {
        const ctx = {
          source_slug: page.slug,
          source_keyword: page.primaryKeyword,
          source: "hireeva_seo_sprint",
          captured_at: new Date().toISOString(),
        };
        localStorage.setItem("hireeva:last_seo_checkout_context", JSON.stringify(ctx));
      }
    } catch {}
  }, [page.slug, page.primaryKeyword, page.title]);

  async function handleCta(label: string) {
    track("seo_cta_click", { slug: page.slug, keyword: page.primaryKeyword, label, source: "hireeva_seo_sprint", source_slug: page.slug, source_keyword: page.primaryKeyword });
    track("checkout_started", { source_slug: page.slug, source_keyword: page.primaryKeyword, source: "hireeva_seo_sprint", plan: page.ctaPlan ?? "solo" });
    openCallMeNow(`seo_${page.slug}`);
  }

  const jsonLd = buildJsonLd(page);

  return (
    <div style={{ background: PALETTE.bg, color: PALETTE.ink, fontFamily: FONT_BODY, minHeight: "100vh" }}>
      <Helmet>
        <html lang="en" />
        <title>{page.title}</title>
        <meta name="description" content={page.description} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={page.title} />
        <meta name="twitter:description" content={page.description} />
        <meta name="twitter:url" content={url} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <SiteNav product="software" primary={{ label: "See pricing", to: "/#pricing" }} callSource="seo_nav" cobrainHint />
      <SiteNavSpacer />

      <main id="page-main">
      <section style={{ padding: "72px 24px 56px" }}>
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          {page.kicker && (
            <div style={{ fontFamily: FONT_MONO, fontSize: 12, letterSpacing: "0.18em", color: PALETTE.amber, marginBottom: 18 }}>
              {page.kicker}
            </div>
          )}
          <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: "clamp(40px, 6vw, 72px)", lineHeight: 1.05, margin: 0, color: PALETTE.ink }}>
            {page.h1}
          </h1>
          <p style={{ marginTop: 24, fontSize: 20, lineHeight: 1.5, color: PALETTE.ink2, maxWidth: 760 }}>{page.lede}</p>
          <p style={{ marginTop: 16, fontFamily: FONT_MONO, fontSize: 13, color: PALETTE.muted, letterSpacing: "0.04em" }}>
            Not a chatbot. Not just a receptionist. An AI operator for your business, deployed by your own engineer — from $1,500/month.
          </p>
          <div style={{ marginTop: 32, display: "flex", gap: 14, flexWrap: "wrap" }}>
            <button onClick={() => handleCta("hero_primary")}
              style={{ padding: "14px 26px", background: PALETTE.ink, color: "#fff", border: 0, borderRadius: 999, fontSize: 15, cursor: "pointer" }}>
              {page.ctaLabel ?? "Talk to an engineer"}
            </button>
            <Link to="/#capability" onClick={() => track("seo_cta_click", { slug: page.slug, label: "see_what_she_does", source: "hireeva_seo_sprint", source_slug: page.slug, source_keyword: page.primaryKeyword })}
              style={{ padding: "14px 26px", background: "transparent", color: PALETTE.ink, border: `1px solid ${PALETTE.line}`, borderRadius: 999, fontSize: 15, textDecoration: "none" }}>
              See everything she does →
            </Link>
          </div>
          {page.audience && (
            <p style={{ marginTop: 28, fontSize: 14, color: PALETTE.muted, fontStyle: "italic" }}>For {page.audience}</p>
          )}
        </div>
      </section>

      <section style={{ padding: "64px 24px", background: PALETTE.bgAlt, borderTop: `1px solid ${PALETTE.line}`, borderBottom: `1px solid ${PALETTE.line}` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ fontFamily: FONT_MONO, fontSize: 12, letterSpacing: "0.18em", color: PALETTE.amber, marginBottom: 12 }}>WHAT EVA DOES</div>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: "clamp(32px, 4.5vw, 48px)", margin: "0 0 32px" }}>
            An AI-run operation, built by an engineer who knows your business.
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 18 }}>
            {page.useCases.map((u) => (
              <article key={u.title} style={{ background: PALETTE.card, padding: 22, borderRadius: 14, border: `1px solid ${PALETTE.line}` }}>
                <h3 style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: 22, margin: "0 0 8px" }}>{u.title}</h3>
                <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.55, color: PALETTE.ink2 }}>{u.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {page.comparison && (
        <section style={{ padding: "64px 24px" }}>
          <div style={{ maxWidth: 1000, margin: "0 auto" }}>
            <h2 style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: "clamp(28px, 4vw, 42px)", margin: "0 0 24px" }}>
              {page.comparison.headline}
            </h2>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14.5 }}>
                <thead>
                  <tr style={{ textAlign: "left", borderBottom: `1px solid ${PALETTE.line}` }}>
                    <th style={{ padding: "12px 8px", color: PALETTE.muted, fontWeight: 500 }}></th>
                    <th style={{ padding: "12px 8px", color: PALETTE.ink }}>Eva</th>
                    <th style={{ padding: "12px 8px", color: PALETTE.muted }}>Alternatives</th>
                  </tr>
                </thead>
                <tbody>
                  {page.comparison.rows.map((r) => (
                    <tr key={r.label} style={{ borderBottom: `1px solid ${PALETTE.line}` }}>
                      <td style={{ padding: "14px 8px", color: PALETTE.muted }}>{r.label}</td>
                      <td style={{ padding: "14px 8px", color: PALETTE.ink, fontWeight: 600 }}>{r.eva}</td>
                      <td style={{ padding: "14px 8px", color: PALETTE.ink2 }}>{r.them}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      <section style={{ padding: "64px 24px", background: PALETTE.bgAlt, borderTop: `1px solid ${PALETTE.line}` }}>
        <div style={{ maxWidth: 880, margin: "0 auto" }}>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: "clamp(28px, 4vw, 42px)", margin: "0 0 28px" }}>Frequently asked.</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {page.faqs.map((f) => (
              <details key={f.q} style={{ background: PALETTE.card, border: `1px solid ${PALETTE.line}`, borderRadius: 12, padding: "16px 18px" }}>
                <summary style={{ cursor: "pointer", fontWeight: 600, fontSize: 16, color: PALETTE.ink }}>{f.q}</summary>
                <p style={{ marginTop: 12, marginBottom: 0, color: PALETTE.ink2, lineHeight: 1.6, fontSize: 14.5 }}>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {page.related.length > 0 && (
        <section style={{ padding: "56px 24px" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <div style={{ fontFamily: FONT_MONO, fontSize: 12, letterSpacing: "0.18em", color: PALETTE.amber, marginBottom: 12 }}>RELATED</div>
            <h2 style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: "clamp(26px, 3.5vw, 36px)", margin: "0 0 22px" }}>Keep exploring.</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 12 }}>
              {page.related.map((rs) => {
                const r = getSeoPage(rs);
                if (!r) return null;
                return (
                  <Link key={rs} to={`/${rs}.html`} style={{ display: "block", padding: 16, border: `1px solid ${PALETTE.line}`, borderRadius: 12, background: PALETTE.card, textDecoration: "none", color: PALETTE.ink }}>
                    <div style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: 18 }}>{r.h1.replace(/\.$/, "")}</div>
                    <div style={{ fontSize: 13, color: PALETTE.muted, marginTop: 6 }}>/{rs}.html</div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section style={{ padding: "72px 24px", textAlign: "center", borderTop: `1px solid ${PALETTE.line}`, background: PALETTE.bg }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: "clamp(32px, 5vw, 56px)", margin: 0 }}>
            Hire Eva. Get your weekends back.
          </h2>
          <p style={{ marginTop: 14, color: PALETTE.ink2 }}>Live in days with your own engineer. From $1,500/month.</p>
          <button onClick={() => handleCta("footer_primary")}
            style={{ marginTop: 26, padding: "16px 32px", background: PALETTE.ink, color: "#fff", border: 0, borderRadius: 999, fontSize: 16, cursor: "pointer" }}>
            Hire Eva →
          </button>
        </div>
      </section>

      </main>
      <SiteFooter product="software" callSource="seo_footer" />
    </div>
  );
}
